import React, { useContext, useState, useRef, useEffect } from "react";
import AppContext from "../../context/AppContext";
import { FiGlobe } from "react-icons/fi";
import classNames from "classnames";

const LangComp = ({
  toggleLangMode,
  isNavbarOpen,
  isShowIcon = "true",
  languageDataList,
  menuPosition = "fixed",
  variant = "light", // NEW: "light" (default, unchanged) | "dark"
}) => {
  const { theme, langMode } = useContext(AppContext);
  const data = languageDataList
    ? languageDataList
    : [
        { id: 1, lang: "en", langText: "Eng" },
        { id: 2, lang: "ur", langText: "Ur" },
        { id: 3, lang: "ar", langText: "Ar" },
      ];

  const [selectedLang, setSelectedLang] = useState(langMode || "en");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleChange = (lang) => {
    setSelectedLang(lang);
    toggleLangMode(lang);
    setIsOpen(false);
  };

  const getLangText = (lang) => {
    const selected = data.find((item) => item.lang === lang);
    return selected ? selected.langText : "Eng";
  };

  useEffect(() => {
    setSelectedLang(langMode || "en");
  }, [langMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDark = variant === "dark";
  const isRtl = langMode === "ar" || langMode === "ur";
  const useFixedMenu = Boolean(isShowIcon) && menuPosition === "fixed";

  return (
    <div
      className={`relative flex items-center gap-2 ${!isShowIcon ? "rounded-md" : ""}`}
      ref={dropdownRef}
    >
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Select Language"
        className={classNames(
          "flex items-center py-1 transition-all rounded-md outline-none focus:outline-none",
          !isDark && (theme === "dark" ? "bg-gray-800 text-white" : "bg-transparent text-black"),
          !isDark && "hover:bg-gray-200 dark:hover:bg-gray-700",
          isShowIcon
            ? isDark
              ? "px-3.5 py-2 rounded-full gap-2 border border-white/25 bg-white/10 backdrop-blur-sm text-white hover:bg-white/15 active:scale-95 transition-all duration-200"
              : "px-4 py-2 rounded-2xl gap-2 border border-[#E6EAF2] bg-white text-[#5F6C8D] hover:bg-white dark:hover:bg-gray-800"
            : "px-5 gap-2 !bg-transparent border focus:border-gray-300 text-gray-700"
        )}
      >
        {isShowIcon ? <FiGlobe size={20} className={isDark ? "text-white" : ""} /> : null}
        <span
          className={classNames(
            isShowIcon ? "text-[15px] font-semibold" : "text-sm",
            isDark && "text-white"
          )}
        >
          {getLangText(selectedLang)}
        </span>
        {!isShowIcon && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-4"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        )}
      </button>
      {isOpen && (
        <div
          role="menu"
          style={{ zIndex: useFixedMenu ? 99999 : 70, position: useFixedMenu ? "fixed" : "absolute" }}
          className={classNames(
            "cursor-pointer rounded-lg shadow-xl p-1 text-sm transition-all border",
            theme === "high-contrast"
              ? "bg-black text-white border-white border-2"
              : theme === "dark"
              ? "bg-slate-900 text-slate-100 border-slate-700"
              : "bg-white text-gray-800 border-slate-200",
            useFixedMenu && (isNavbarOpen ? "top-32" : "top-10"),
            !useFixedMenu && "top-full mt-2",
            !useFixedMenu && (isRtl ? "left-0" : "right-0"),
            !isShowIcon ? "w-28" : "min-w-[120px]"
          )}
        >
          {data.map((item) => (
            <div
              key={item.id}
              role="menuitem"
              onClick={() => handleChange(item.lang)}
              className={classNames(
                "flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md transition-colors",
                theme === "high-contrast"
                  ? selectedLang === item.lang
                    ? "bg-yellow-400 text-black font-semibold"
                    : "text-white hover:bg-zinc-800"
                  : theme === "dark"
                  ? selectedLang === item.lang
                    ? "bg-indigo-950/40 text-indigo-400 font-semibold"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  : selectedLang === item.lang
                  ? "bg-indigo-50 text-indigo-600 font-semibold"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              )}
            >
              {item.langText}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LangComp;
